import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Thunk to refund/convert a payment
export const refundPayment = createAsyncThunk(
  "payments/refundPayment",
  async (
    { paymentId, refundType, paymentMethod, reason },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/invoices/payments/${paymentId}/refund`,
        {
          refundType,
          paymentMethod,
          reason
        },
        config
      );

      console.log("[Payment Refund Thunk] Payment refund response:", response.data);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk to refund a deposit
export const refundDeposit = createAsyncThunk(
  "payments/refundDeposit",
  async (
    { depositId, refundAmount, paymentMethod, reason },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/deposits/${depositId}/refund`,
        {
          refundAmount,
          paymentMethod,
          reason
        },
        config
      );

      console.log("[Deposit Refund Thunk] Deposit refund response:", response.data);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Thunk to create a payment
export const createPayment = createAsyncThunk(
  "payments/createPayment",
  async (
    { invoiceId, paymentMethod, paymentAmount, paymentDate, createdBy },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/payments`,
        {
          invoiceId,
          paymentMethod,
          amount: paymentAmount,
          paymentDate,
          createdBy,
        },
        config
      );

      // Include a success message in the return value
      console.log("Create payment thunk response:", response.data);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to fetch all payments
export const fetchAllPayments = createAsyncThunk(
  "payments/fetchAllPayments",
  async (
    { page = 1, limit = 10, sortKey = null, sortDirection = null, dateFilter = "7" },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/payments`, {
        ...config,
        params: {
          page,
          limit,
          sortKey,
          sortDirection,
          dateFilter,
        },
      });
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to fetch payments for an invoice
export const fetchPaymentsByInvoice = createAsyncThunk(
  "payments/fetchPaymentsByInvoice",
  async (invoiceId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/payments/${invoiceId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to fetch payments by client ID
export const fetchPaymentsByClientId = createAsyncThunk(
  "payments/fetchPaymentsByClientId",
  async (clientId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/payments/client/${clientId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const fetchInvoicePaymentsByAppointmentId = createAsyncThunk(
  "payments/fetchInvoicePaymentsByAppointmentId",
  async (appointmentId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/payments/appointment/${appointmentId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to edit a payment
export const editPayment = createAsyncThunk(
  "payments/editPayment",
  async (
    { paymentId, paymentMethod, paymentAmount, paymentDate },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/payments/${paymentId}`,
        { paymentMethod, amount: paymentAmount, paymentDate }, // Include paymentDate
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to collect a deposit payment
export const collectDepositPayment = createAsyncThunk(
  "payments/collectDepositPayment",
  async (
    { paymentMethod, paymentAmount, paymentDate, createdBy, clientId, clinicId },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/payments/deposit`,
        {
          paymentMethod,
          amount: paymentAmount,
          paymentDate,
          createdBy,
          clientId,
          clinic: clinicId, // Backend expects 'clinic' parameter
        },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Thunk to fetch deposits for a client
export const fetchDeposits = createAsyncThunk(
  "payments/fetchDeposits",
  async (clientId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/payments/deposits/${clientId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Update the applyDepositToPayment thunk to handle multiple deposits
export const applyDepositToPayment = createAsyncThunk(
  "payments/useDepositForPayment",
  async (
    { depositIds, invoiceId, invoiceNumber, amounts, userId },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log("[DEBUG] Sending deposit payment request:", {
        depositIds,
        invoiceId,
        invoiceNumber,
        amounts,
        userId
      });

      // Send all deposits and amounts in a single request
      const response = await axios.post(
        `${baseURL}/api/payments/use-deposit`,
        {
          deposits: depositIds.map((depositId, index) => ({
            depositId,
            amount: amounts[index],
            invoiceNumber: invoiceNumber, // Ensure invoiceNumber is passed for each deposit
          })),
          invoiceId,
          userId,
        },
        config
      );

      return response.data;
    } catch (error) {
      console.error("[DEBUG] Error in applyDepositToPayment:", error.response?.data || error);
      return handleApiError(error, rejectWithValue);
    }
  }
);

export const manualOverridePayment = createAsyncThunk(
  "payments/manualOverridePayment",
  async ({ paymentId, updateData }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // Using PATCH for partial updates
      const response = await axios.patch(
        `${baseURL}/api/payments/manual-override/${paymentId}`,
        updateData,
        config
      );

      return response.data.payment;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
